'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

function useIntersectionObserver(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Our Wines', href: '/wines' },
    { label: 'Winemaking', href: '/winemaking' },
    { label: 'Team', href: '/team' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <>
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-500"
        style={{ background: 'rgba(10,7,5,0.97)', backdropFilter: 'blur(20px)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}>
        <button className="absolute top-6 right-6 p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
        </button>
        <nav className="flex flex-col gap-8 text-center">
          {navLinks?.map((link) => (
            <Link key={link?.href + link?.label} href={link?.href}
              className="font-display text-3xl font-medium italic text-[#F5EDD6] hover:text-[#C9A84C] transition-colors duration-300"
              onClick={() => setMenuOpen(false)}>{link?.label}</Link>
          ))}
        </nav>
        <div className="mt-10 text-[#9A8A72] text-xs font-sans uppercase tracking-[0.3em]">Est. 1850 · Pauillac, France</div>
      </div>

      <nav className="fixed z-50 top-0 inset-x-0 px-6 py-3 flex items-center justify-between transition-all duration-500"
        style={{ background: scrolled ? 'rgba(10,7,5,0.85)' : 'transparent', backdropFilter: scrolled ? 'blur(12px)' : 'none', borderBottom: scrolled ? '1px solid rgba(46,26,16,0.6)' : 'none' }}>
        <Link href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
              <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
              <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
            </svg>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
            <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks?.map((link) => (
            <Link key={link?.href + link?.label} href={link?.href}
              className="text-sm font-medium text-[#9A8A72] hover:text-[#F5EDD6] transition-colors duration-300 relative group">
              {link?.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link href="/contact" className="hidden md:inline-flex items-center gap-2 px-5 py-2 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Visit Estate
          </Link>
          <button className="lg:hidden p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
          </button>
        </div>
      </nav>
    </>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#2E1A10] bg-[#0A0705] pt-16 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-12">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
                <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
                <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
                <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
              </div>
            </div>
            <p className="text-[#9A8A72] text-sm leading-relaxed italic font-display">A historic Bordeaux château crafting wines of extraordinary depth and elegance since 1850.</p>
            <p className="text-[#9A8A72] text-xs mt-4 font-sans">Route des Châteaux, Saint Lambert<br />33250 Pauillac, Gironde, France</p>
          </div>
          <div className="flex flex-wrap gap-x-16 gap-y-8">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Estate</span>
              <Link href="/" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Home</Link>
              <Link href="/about" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Heritage</Link>
              <Link href="/winemaking" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Winemaking</Link>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Discover</span>
              <Link href="/wines" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Wines</Link>
              <Link href="/team" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Team & Investors</Link>
              <Link href="/contact" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Visit Us</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2E1A10] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-[#9A8A72] font-sans">© 2025 圣兰博之围 · Château Baron, Pauillac AOC · All rights reserved</span>
        </div>
      </div>
    </footer>
  );
}

const ESTATE_TEAM = [
  { name: 'Élodie Marchais', role: 'Maître de Chai', bio: 'Élodie has overseen every vintage since 2003. Trained at the École Nationale Supérieure d\'Agronomie, she is the custodian of the 圣兰博之围 style.' },
  { name: 'Jean-Baptiste Renard', role: 'Directeur du Vignoble', bio: 'Jean-Baptiste manages all 45 hectares with a biodynamic philosophy, believing that healthy soils produce wines of extraordinary depth and longevity.' },
  { name: 'Marguerite Fontaine', role: 'Directrice Commerciale', bio: 'Marguerite leads 圣兰博之围\'s global commercial strategy, placing the estate\'s wines in the finest restaurants and cellars across four continents.' },
  { name: 'Thomas Leclerc', role: 'Oenologue Conseil', bio: 'Thomas consults on each harvest and blending session, bringing his expertise in Cabernet Sauvignon extraction and oak management to bear on every cuvée.' },
];

function TripperCard() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  const [activePhoto, setActivePhoto] = useState(0);
  const photos = [
    { src: '/assets/images/0215d316f183c14795d2e4f2501b11deb447dbd777ca9d57cc307a3d0a93f042-1788705714467.jpg', alt: 'Tripper, Lead Investor, portrait photo one' },
    { src: '/assets/images/edc93194a3ba572fa6557103ea1c78e8bf9cce789136c201d4e8b0177d89df2d-1788705714491.jpg', alt: 'Tripper, Lead Investor, portrait photo two' },
    { src: '/assets/images/4bc528d5b3d0049de489a4bcdabc94fe588cb6b205043c43fc9639783d161566-1788705714570.jpg', alt: 'Tripper, Lead Investor, portrait photo three' },
  ];

  useEffect(() => {
    const timer = setInterval(() => setActivePhoto(prev => (prev + 1) % photos?.length), 3500);
    return () => clearInterval(timer);
  }, [photos?.length]);

  const stats = [
    { label: 'Joined Board', value: '2018' },
    { label: 'Vision', value: 'Global Distribution' },
    { label: 'Heritage', value: '175+ Years' },
    { label: 'Appellation', value: 'Pauillac AOC' },
  ];

  return (
    <section className="py-16 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-10">
        <span className="font-sans text-sm font-semibold tracking-[0.25em] text-[#C9A84C]">01 — LEAD INVESTOR</span>
      </div>
      <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start"
        style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        {/* Photo slideshow */}
        <div className="flex flex-col gap-3">
          <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
            {photos?.map((photo, i) => (
              <img key={i} src={photo?.src} alt={photo?.alt}
                className="w-full h-full object-cover object-top absolute inset-0"
                style={{ filter: 'grayscale(100%)', opacity: i === activePhoto ? 1 : 0, transition: 'opacity 0.8s ease' }}
                loading="lazy" />
            ))}
          </div>
          <div className="flex gap-2">
            {photos?.map((photo, i) => (
              <button key={i} onClick={() => setActivePhoto(i)}
                className="relative overflow-hidden flex-1 focus:outline-none"
                style={{ aspectRatio: '1/1', opacity: i === activePhoto ? 1 : 0.45, transition: 'opacity 0.3s ease' }}
                aria-label={`View photo ${i + 1}`}>
                <img src={photo?.src} alt={photo?.alt} className="w-full h-full object-cover object-top" style={{ filter: 'grayscale(100%)' }} />
                {i === activePhoto && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9A84C]" />}
              </button>
            ))}
          </div>
        </div>
        {/* Content */}
        <div className="flex flex-col justify-start pt-4 lg:pt-8">
          <h2 className="font-display font-medium text-[#F5EDD6] leading-none mb-4" style={{ fontSize: 'clamp(3rem, 7vw, 6rem)' }}>Tripper</h2>
          <div className="flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-[#C9A84C] shrink-0" />
            <span className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-[#C9A84C]">Lead Investor & Strategic Partner</span>
          </div>
          <blockquote className="font-display italic text-lg md:text-xl text-[#F5EDD6] leading-relaxed mb-8">
            "圣兰博之围 represents everything I believe in — the intersection of heritage, excellence, and the patient pursuit of something truly extraordinary."
          </blockquote>
          <p className="text-[#9A8A72] text-base leading-relaxed mb-6">
            Tripper's investment in 圣兰博之围 reflects a deep conviction that the world's finest wines are not merely beverages — they are living documents of place and time. With a portfolio spanning luxury hospitality and fine arts, Tripper brings a global perspective to the ancient traditions of Pauillac winemaking.
          </p>
          <p className="text-[#9A8A72] text-base leading-relaxed mb-10">
            Since joining the 圣兰博之围 board in 2018, Tripper has been instrumental in expanding the estate's international presence while preserving the artisanal practices that have defined its character for 175 years. His vision for the château combines respect for tradition with the ambition to ensure 圣兰博之围's wines reach collectors and connoisseurs worldwide.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {stats?.map(s => (
              <div key={s?.label} className="border-l-2 border-[#C9A84C]/30 pl-4">
                <span className="font-display text-lg font-medium text-[#C9A84C] block">{s?.value}</span>
                <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#9A8A72]">{s?.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-16 h-px bg-[#2E1A10]" />
    </section>
  );
}

function EstateTeamSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  return (
    <section className="py-16 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-12">
        <span className="font-sans text-sm font-semibold tracking-[0.25em] text-[#C9A84C]">02 — THE ESTATE TEAM</span>
      </div>
      <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)' }}>
        The Craftspeople Behind Every Bottle
      </h2>
      <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 mt-12 border border-[#2E1A10]"
        style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        {ESTATE_TEAM?.map((member, i) => (
          <div key={member?.name} className="border-r border-[#2E1A10] last:border-r-0 p-8 flex flex-col"
            style={{ transitionDelay: `${i * 0.1}s` }}>
            <div className="w-12 h-12 border border-[#C9A84C]/30 flex items-center justify-center mb-6">
              <span className="font-display text-lg font-medium text-[#C9A84C]">0{i + 1}</span>
            </div>
            <h3 className="font-display font-medium text-[#F5EDD6] text-xl mb-2">{member?.name}</h3>
            <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C] mb-4">{member?.role}</span>
            <p className="text-[#9A8A72] text-sm leading-relaxed">{member?.bio}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function TeamPage() {
  return (
    <main className="bg-[#0A0705] text-[#F5EDD6] overflow-x-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navigation />
      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#C9A84C]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">The People Behind the Wine</span>
        </div>
        <h1 className="font-display font-medium text-[#F5EDD6] leading-none select-none" style={{ fontSize: 'clamp(6rem, 18vw, 18rem)' }}>
          Team
        </h1>
        <p className="font-display italic text-lg md:text-xl text-[#F5EDD6]/70 max-w-xl leading-relaxed mt-6">
          Great wine is a collaboration — between the land, the seasons, and the people who dedicate their lives to its perfection.
        </p>
      </section>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-16"><div className="h-px bg-[#2E1A10]" /></div>
      <TripperCard />
      <EstateTeamSection />
      <Footer />
    </main>
  );
}
